// UpgradeGate — what a Basic restaurant sees in place of a Premium page
// (billing-signup.md §5.5). Wraps a page body; renders it when the server has
// answered Premium, a small spinner until it answers, and the upgrade card
// once it has answered Basic.
//
// It fails open in two cases: the fetch failed, or there is no signing
// context to fetch with (bunker offline). The server enforces where it
// matters, and a portal that hides everything behind a failed fetch is an
// outage dressed as a gate. It does NOT fail open before the first attempt —
// that painted the page, then swapped it for the spinner a frame later.

import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Sparkles } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useBilling } from '@/state/useBilling'
import { useLoyaltyContext } from '@/hooks/useLoyaltyContext'

export type GateFeature =
  | 'pulse'
  | 'offers'
  | 'events'
  | 'happyHour'
  | 'specials'
  | 'reservations'
  | 'takeout'
  | 'customers'

export const PLAN_PAGE_PATH = '/app/settings/plan'

interface Props {
  feature: GateFeature
  children: React.ReactNode
}

export function UpgradeGate({ feature, children }: Props) {
  const { t } = useTranslation('billing')
  const navigate = useNavigate()
  const { plan, loaded, error } = useBilling()
  const ctx = useLoyaltyContext()

  if (!loaded && !error && ctx) {
    return (
      <div className="flex justify-center px-4 py-12" data-testid="upgrade-gate-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (loaded && plan === 'basic') {
    return (
      <div className="px-4 py-4" data-testid="upgrade-gate">
        <Card className="mx-auto max-w-md text-center">
          <Sparkles className="mx-auto mb-3 h-8 w-8 text-primary" />
          <h2 className="text-lg font-bold text-foreground">
            {t('gateTitle', { feature: t(`gateFeature.${feature}`) })}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('gateBody')}</p>
          <p className="mt-3 text-sm font-semibold text-foreground">
            {t('priceMonthly')} {t('perMonth')} · {t('priceAnnual')} {t('perYear')}
          </p>
          <Button className="mt-4 w-full" onClick={() => navigate(PLAN_PAGE_PATH)}>
            {t('upgrade')}
          </Button>
        </Card>
      </div>
    )
  }

  return <>{children}</>
}
